import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { IconFilter, IconPlus, IconReceipt, IconSearch } from '../components/icons';
import { LargeTitle } from '../components/layout';
import { ReceiptGroups, categoryName } from '../components/receipts';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Select,
  Sheet,
  Skeleton,
} from '../components/ui';
import { useI18n } from '../i18n';
import type { ExpenseFilters, ExpenseStatus, PaymentMethod } from '../lib/api';
import { useLedgerContext } from '../lib/ledger-context';
import { useCategories, useExpenses, usePeople } from '../lib/queries';
import { accentVar, cn, useDebounced } from '../lib/ui';

const STATUSES: ExpenseStatus[] = ['open', 'partial', 'reimbursed', 'ineligible'];
const PAYMENTS: PaymentMethod[] = ['out_of_pocket', 'hsa_card', 'other'];
const SORTS = ['date_desc', 'date_asc', 'amount_desc', 'amount_asc', 'added_desc'] as const;

/**
 * Filters live in the URL rather than component state. That makes the back
 * button work the way people expect, lets "receipts missing a document" be a
 * link from the dashboard, and means a filtered view can simply be sent to
 * someone else in the household.
 */
function useFilterState() {
  const [params, setParams] = useSearchParams();

  const list = (key: string) => params.get(key)?.split(',').filter(Boolean) ?? [];

  const filters: ExpenseFilters = {
    q: params.get('q') ?? undefined,
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    personId: list('personId'),
    categoryId: list('categoryId'),
    status: list('status') as ExpenseStatus[],
    paymentMethod: list('paymentMethod') as PaymentMethod[],
    minCents: params.get('minCents') ? Number(params.get('minCents')) : undefined,
    maxCents: params.get('maxCents') ? Number(params.get('maxCents')) : undefined,
    hasDocument: params.get('hasDocument') ? params.get('hasDocument') === 'true' : undefined,
    sort: params.get('sort') ?? 'date_desc',
    pageSize: 100,
  };

  const update = (patch: Record<string, string | string[] | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      const serialised = Array.isArray(value) ? value.join(',') : value;
      if (!serialised) next.delete(key);
      else next.set(key, serialised);
    }
    setParams(next, { replace: true });
  };

  const clear = () => setParams(new URLSearchParams(), { replace: true });

  // The search box is not a "filter" the user needs reminding about — it is
  // visible on screen. Sorting is not a filter either.
  const activeCount = [
    filters.from,
    filters.to,
    filters.personId?.length ? '1' : undefined,
    filters.categoryId?.length ? '1' : undefined,
    filters.status?.length ? '1' : undefined,
    filters.paymentMethod?.length ? '1' : undefined,
    filters.minCents !== undefined ? '1' : undefined,
    filters.maxCents !== undefined ? '1' : undefined,
    filters.hasDocument !== undefined ? '1' : undefined,
  ].filter(Boolean).length;

  return { filters, update, clear, activeCount };
}

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function Chip({
  label,
  active,
  color,
  onClick,
}: {
  label: string;
  active: boolean;
  color?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'press inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition-colors',
        active
          ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
          : 'border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--surface-sunken)]',
      )}
    >
      {color ? (
        <span className="h-2 w-2 rounded-full" style={{ background: color }} aria-hidden="true" />
      ) : null}
      {label}
    </button>
  );
}

function FilterSheet({
  open,
  onClose,
  filters,
  update,
  clear,
}: {
  open: boolean;
  onClose: () => void;
  filters: ExpenseFilters;
  update: (patch: Record<string, string | string[] | undefined>) => void;
  clear: () => void;
}) {
  const { t, locale } = useI18n();
  const { currentId } = useLedgerContext();
  const people = usePeople(currentId ?? undefined);
  const categories = useCategories();

  const thisYear = new Date().getUTCFullYear();

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('common.filters')}
      size="lg"
      footer={
        <div className="flex gap-2.5">
          <Button variant="secondary" fullWidth onClick={clear}>
            {t('common.clearAll')}
          </Button>
          <Button fullWidth onClick={onClose}>
            {t('common.done')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6 pt-1">
        <section>
          <h3 className="mb-2.5 text-[13px] font-semibold text-[var(--ink-secondary)]">
            {t('receipts.filter.dateRange')}
          </h3>
          <div className="mb-3 flex flex-wrap gap-2">
            <Chip
              label={t('receipts.filter.anyTime')}
              active={!filters.from && !filters.to}
              onClick={() => update({ from: undefined, to: undefined })}
            />
            <Chip
              label={t('receipts.filter.thisYear')}
              active={filters.from === `${thisYear}-01-01`}
              onClick={() => update({ from: `${thisYear}-01-01`, to: `${thisYear}-12-31` })}
            />
            <Chip
              label={t('receipts.filter.lastYear')}
              active={filters.from === `${thisYear - 1}-01-01`}
              onClick={() => update({ from: `${thisYear - 1}-01-01`, to: `${thisYear - 1}-12-31` })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('receipts.filter.from')}>
              <Input
                type="date"
                value={filters.from ?? ''}
                onChange={(event) => update({ from: event.target.value })}
              />
            </Field>
            <Field label={t('receipts.filter.to')}>
              <Input
                type="date"
                value={filters.to ?? ''}
                onChange={(event) => update({ to: event.target.value })}
              />
            </Field>
          </div>
        </section>

        {/* People belong to a ledger, so this filter only means something
            while one is selected. */}
        <section hidden={!currentId}>
          <h3 className="mb-2.5 text-[13px] font-semibold text-[var(--ink-secondary)]">
            {t('receipts.filter.person')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {people.data?.people.map((person) => (
              <Chip
                key={person.id}
                label={person.name}
                color={accentVar(person.color)}
                active={filters.personId?.includes(person.id) ?? false}
                onClick={() => update({ personId: toggle(filters.personId ?? [], person.id) })}
              />
            ))}
          </div>
        </section>

        <section>
          <h3 className="mb-2.5 text-[13px] font-semibold text-[var(--ink-secondary)]">
            {t('receipts.filter.category')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {categories.data?.categories
              .filter((category) => !category.archivedAt)
              .map((category) => (
                <Chip
                  key={category.id}
                  label={categoryName(category, locale)}
                  color={accentVar(category.color)}
                  active={filters.categoryId?.includes(category.id) ?? false}
                  onClick={() =>
                    update({ categoryId: toggle(filters.categoryId ?? [], category.id) })
                  }
                />
              ))}
          </div>
        </section>

        <section>
          <h3 className="mb-2.5 text-[13px] font-semibold text-[var(--ink-secondary)]">
            {t('receipts.filter.status')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((status) => (
              <Chip
                key={status}
                label={t(`status.${status}` as 'status.open')}
                active={filters.status?.includes(status) ?? false}
                onClick={() => update({ status: toggle(filters.status ?? [], status) })}
              />
            ))}
          </div>
        </section>

        <section>
          <h3 className="mb-2.5 text-[13px] font-semibold text-[var(--ink-secondary)]">
            {t('receipts.filter.payment')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {PAYMENTS.map((method) => (
              <Chip
                key={method}
                label={t(`payment.${method}` as 'payment.out_of_pocket')}
                active={filters.paymentMethod?.includes(method) ?? false}
                onClick={() =>
                  update({ paymentMethod: toggle(filters.paymentMethod ?? [], method) })
                }
              />
            ))}
          </div>
        </section>

        <section>
          <h3 className="mb-2.5 text-[13px] font-semibold text-[var(--ink-secondary)]">
            {t('receipts.filter.documents')}
          </h3>
          <div className="space-y-2.5">
            <Checkbox
              checked={filters.hasDocument === true}
              onChange={(checked) => update({ hasDocument: checked ? 'true' : undefined })}
              label={t('receipts.filter.withDocuments')}
            />
            <Checkbox
              checked={filters.hasDocument === false}
              onChange={(checked) => update({ hasDocument: checked ? 'false' : undefined })}
              label={t('receipts.filter.withoutDocuments')}
            />
          </div>
        </section>

        <section>
          <h3 className="mb-2.5 text-[13px] font-semibold text-[var(--ink-secondary)]">
            {t('receipts.filter.amount')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('receipts.filter.min')}>
              <Input
                inputMode="decimal"
                value={filters.minCents !== undefined ? String(filters.minCents / 100) : ''}
                onChange={(event) =>
                  update({
                    minCents: event.target.value
                      ? String(Math.round(Number(event.target.value) * 100))
                      : undefined,
                  })
                }
              />
            </Field>
            <Field label={t('receipts.filter.max')}>
              <Input
                inputMode="decimal"
                value={filters.maxCents !== undefined ? String(filters.maxCents / 100) : ''}
                onChange={(event) =>
                  update({
                    maxCents: event.target.value
                      ? String(Math.round(Number(event.target.value) * 100))
                      : undefined,
                  })
                }
              />
            </Field>
          </div>
        </section>
      </div>
    </Sheet>
  );
}

export function ReceiptsScreen() {
  const { t, money } = useI18n();
  const { filterIds } = useLedgerContext();
  const { filters, update, clear, activeCount } = useFilterState();
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState(filters.q ?? '');

  const debouncedSearch = useDebounced(search, 300);
  const query = useExpenses(
    useMemo(
      () => ({ ...filters, ledgerId: filterIds, q: debouncedSearch }),
      [filters, filterIds, debouncedSearch],
    ),
  );

  const totals = query.data?.totals;
  const expenses = query.data?.expenses ?? [];
  const isFiltered = activeCount > 0 || Boolean(debouncedSearch);

  return (
    <>
      <LargeTitle
        title={t('receipts.title')}
        action={
          <Link to="/add">
            <Button size="sm" icon={<IconPlus size={17} />}>
              <span className="hidden sm:inline">{t('nav.add')}</span>
            </Button>
          </Link>
        }
      >
        <div className="mt-4 flex gap-2">
          <div className="relative flex-1">
            <IconSearch
              size={17}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]"
            />
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('receipts.searchPlaceholder')}
              className="pl-9"
              aria-label={t('common.search')}
            />
          </div>
          <Button
            variant="secondary"
            onClick={() => setShowFilters(true)}
            icon={<IconFilter size={17} />}
            aria-label={t('common.filters')}
          >
            {activeCount > 0 ? (
              <span className="numeric text-[13px] font-semibold text-[var(--accent)]">
                {activeCount}
              </span>
            ) : null}
          </Button>
        </div>

        {totals && totals.count > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[var(--ink-secondary)]">
            <span>{t('receipts.resultCount', { count: totals.count })}</span>
            <span className="numeric">
              {t('receipts.totalInView')} {money(totals.totalCents, { round: true })}
            </span>
            {totals.outstandingCents > 0 ? (
              <span className="numeric text-[var(--accent)]">
                {t('receipts.claimableInView')} {money(totals.outstandingCents, { round: true })}
              </span>
            ) : null}
          </div>
        ) : null}

        {activeCount > 0 ? (
          <button
            type="button"
            onClick={clear}
            className="mt-2 inline-flex items-center gap-1.5 text-[13px] text-[var(--accent)]"
          >
            <Badge tone="accent">{t('receipts.filter.activeCount', { count: activeCount })}</Badge>
            {t('common.clearAll')}
          </button>
        ) : null}
      </LargeTitle>

      <div className="pt-3">
        {query.isLoading ? (
          <div className="space-y-3 px-4 lg:px-8">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-[72px] w-full" />
            ))}
          </div>
        ) : expenses.length === 0 ? (
          <div className="px-4 lg:px-8">
            <Card>
              <EmptyState
                icon={<IconReceipt size={38} />}
                title={isFiltered ? t('receipts.emptyFilteredTitle') : t('receipts.emptyTitle')}
                body={isFiltered ? t('receipts.emptyFilteredBody') : t('receipts.emptyBody')}
                action={
                  isFiltered ? (
                    <Button variant="secondary" onClick={clear}>
                      {t('common.clearAll')}
                    </Button>
                  ) : (
                    <Link to="/add">
                      <Button icon={<IconPlus size={18} />}>{t('dashboard.emptyAction')}</Button>
                    </Link>
                  )
                }
              />
            </Card>
          </div>
        ) : (
          <>
            <div className="mb-3 flex justify-end px-4 lg:px-8">
              <Select
                value={filters.sort}
                onChange={(event) => update({ sort: event.target.value })}
                aria-label={t('receipts.sort.label')}
                className="w-auto py-1.5 text-[13px]"
              >
                {SORTS.map((sort) => (
                  <option key={sort} value={sort}>
                    {t(`receipts.sort.${sort}` as 'receipts.sort.date_desc')}
                  </option>
                ))}
              </Select>
            </div>
            <ReceiptGroups expenses={expenses} />
            {query.data && query.data.total > expenses.length ? (
              <p className="px-4 py-6 text-center text-[13px] text-[var(--ink-muted)] lg:px-8">
                {expenses.length} {t('common.of')} {query.data.total}
              </p>
            ) : null}
          </>
        )}
      </div>

      <FilterSheet
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        update={update}
        clear={clear}
      />
    </>
  );
}
